import { useCallback, useEffect, type ReactNode } from 'react';
import { useSharedValue, type SharedValue } from 'react-native-reanimated';
import { Flex } from '@banhala/ui-kit';
import type { LikeFolder } from 'shared/src/api/likedFolderService/getLikedFolders/types';

export type Positions = Record<number, number>;

export const DRAGGABLE_ITEM_HEIGHT = 64;

interface RenderItemArgs {
  item: LikeFolder;
  initialIndex: number;
  positions: SharedValue<Positions>;
  itemsCount: number;
  onDragEnd: () => void;
}

interface DraggableFolderListProps {
  items: LikeFolder[];
  renderItem: (args: RenderItemArgs) => ReactNode;
  onReorder: (items: LikeFolder[]) => void;
}

export const DraggableFolderList = ({
  items,
  renderItem,
  onReorder,
}: DraggableFolderListProps) => {
  const buildPositions = useCallback((folders: LikeFolder[]): Positions => {
    const next: Positions = {};
    folders.forEach((folder, index) => {
      next[folder.sno] = index;
    });
    return next;
  }, []);

  const positions = useSharedValue<Positions>(buildPositions(items));

  useEffect(() => {
    positions.value = buildPositions(items);
  }, [items, positions, buildPositions]);

  const handleDragEnd = () => {
    const snapshot = positions.value;
    const sorted = [...items].sort(
      (a, b) => snapshot[a.sno] - snapshot[b.sno],
    );
    if (sorted.some((folder, index) => folder.sno !== items[index].sno)) {
      onReorder(sorted);
    }
  };

  return (
    <Flex height={items.length * DRAGGABLE_ITEM_HEIGHT}>
      {items.map((item, index) =>
        renderItem({
          item,
          initialIndex: index,
          positions,
          itemsCount: items.length,
          onDragEnd: handleDragEnd,
        }),
      )}
    </Flex>
  );
};
